import type { SVGProps } from 'react';

/**
 * The icon set, lifted from the design bundle.
 *
 * One drawing style throughout: a 20-unit box, 1.4 stroke, round caps and
 * joins, `currentColor` so an icon takes the colour of whatever it sits in.
 * Filled shapes appear only where the design uses them — the stop square and
 * the brand mark.
 */

const paths: Record<string, (p: SVGProps<SVGSVGElement>) => JSX.Element> = {
  dashboard: () => (
    <>
      <rect x="3" y="3" width="6" height="6" rx="1.6" stroke="currentColor" strokeWidth="1.4" />
      <rect x="11" y="3" width="6" height="6" rx="1.6" stroke="currentColor" strokeWidth="1.4" />
      <rect x="3" y="11" width="6" height="6" rx="1.6" stroke="currentColor" strokeWidth="1.4" />
      <rect x="11" y="11" width="6" height="6" rx="1.6" stroke="currentColor" strokeWidth="1.4" />
    </>
  ),
  tasks: () => (
    <>
      <path d="M3.4 6.3L5.1 8L8.1 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3.4 13.4L5.1 15.1L8.1 12.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10.8 6.4H16.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M10.8 13.5H16.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </>
  ),
  projects: () => (
    <path
      d="M3 6.4C3 5.63 3.63 5 4.4 5H7.5L9.1 6.8H15.6C16.37 6.8 17 7.43 17 8.2V14.6C17 15.37 16.37 16 15.6 16H4.4C3.63 16 3 15.37 3 14.6V6.4Z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
  ),
  clock: () => (
    <>
      <circle cx="10" cy="10.4" r="6.4" stroke="currentColor" strokeWidth="1.4" />
      <path d="M10 7.2V10.4L12.2 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8.2 2.6H11.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </>
  ),
  calendar: () => (
    <>
      <rect x="3.2" y="5" width="13.6" height="12" rx="2.2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M3.2 8.6H16.8" stroke="currentColor" strokeWidth="1.4" />
      <path d="M7 3.2V5.4M13 3.2V5.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </>
  ),
  /** A coin: a circle with a smaller one inside, the only currency-neutral money sign. */
  finance: () => (
    <>
      <circle cx="10" cy="10" r="6.6" stroke="currentColor" strokeWidth="1.4" />
      <path d="M10 6.6v6.8M8 8.4c0-.9.9-1.3 2-1.3s2 .5 2 1.3-.9 1.1-2 1.4-2 .6-2 1.5.9 1.4 2 1.4 2-.5 2-1.3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </>
  ),
  reports: () => (
    <path
      d="M4 16.2V9.6M8.7 16.2V4.8M13.3 16.2V12M17.6 16.2H2.8"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  ),
  settings: () => (
    <>
      <circle cx="10" cy="10" r="2.7" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M10 2.9V4.7M10 15.3V17.1M4.2 6.3L5.8 7.2M14.2 12.8L15.8 13.7M4.2 13.7L5.8 12.8M14.2 7.2L15.8 6.3"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </>
  ),
  search: () => (
    <>
      <circle cx="9" cy="9" r="5.6" stroke="currentColor" strokeWidth="1.5" />
      <path d="M13.2 13.2L16.8 16.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </>
  ),
  plus: () => (
    <path d="M10 4.6V15.4M4.6 10H15.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  ),
  minus: () => <path d="M4.6 10H15.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />,
  play: () => <path d="M7.4 5.2L14.4 10L7.4 14.8V5.2Z" fill="currentColor" />,
  stop: () => <rect x="6" y="6" width="8" height="8" rx="1.6" fill="currentColor" />,
  chevronDown: () => (
    <path d="M5.8 8.2L10 12.4L14.2 8.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  ),
  chevronRight: () => (
    <path d="M8.2 5.8L12.4 10L8.2 14.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  ),
  chevronLeft: () => (
    <path d="M11.8 5.8L7.6 10L11.8 14.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  ),
  close: () => (
    <path d="M5.5 5.5L14.5 14.5M14.5 5.5L5.5 14.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  ),
  check: () => (
    <path d="M4.8 10.4L8.4 14L15.2 6.6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  ),
  more: () => (
    <>
      <circle cx="5" cy="10" r="1.35" fill="currentColor" />
      <circle cx="10" cy="10" r="1.35" fill="currentColor" />
      <circle cx="15" cy="10" r="1.35" fill="currentColor" />
    </>
  ),
  bell: () => (
    <>
      <path
        d="M5.6 8.6a4.4 4.4 0 1 1 8.8 0v3l1.2 2.2H4.4L5.6 11.6V8.6Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path d="M8.2 16a1.9 1.9 0 0 0 3.6 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </>
  ),
  menu: () => (
    <path
      d="M3.4 6h13.2M3.4 10h13.2M3.4 14h13.2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  ),
  filter: () => (
    <path
      d="M3.6 5.6h12.8L11.4 11v4.2l-2.8-1.6V11L3.6 5.6Z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
  ),
  download: () => (
    <>
      <path d="M10 3.8v8.4M6.6 9.2L10 12.6l3.4-3.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4.4 15.4h11.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </>
  ),
  trash: () => (
    <>
      <path d="M4.6 6.2h10.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path
        d="M6.4 6.2 7 15a1 1 0 0 0 1 .9h4a1 1 0 0 0 1-.9l.6-8.8"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path d="M8.2 6.2V4.8a1 1 0 0 1 1-1h1.6a1 1 0 0 1 1 1v1.4" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </>
  ),
  edit: () => (
    <path
      d="M13.2 4.4a1.7 1.7 0 0 1 2.4 2.4l-8 8L4 15.9l1.1-3.6 8.1-7.9Z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
  ),
  user: () => (
    <>
      <circle cx="10" cy="7.4" r="3" stroke="currentColor" strokeWidth="1.4" />
      <path d="M4.6 16.2a5.4 5.4 0 0 1 10.8 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </>
  ),
  flag: () => (
    <>
      <path d="M5 17V3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M5 4h9.6l-2.2 3.2 2.2 3.2H5" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </>
  ),
  star: () => (
    <path
      d="M10 3.4l2 4.2 4.6.6-3.4 3.2.9 4.6L10 13.8 5.9 16l.9-4.6-3.4-3.2 4.6-.6 2-4.2Z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
  ),
  starFilled: () => (
    <path
      d="M10 3.4l2 4.2 4.6.6-3.4 3.2.9 4.6L10 13.8 5.9 16l.9-4.6-3.4-3.2 4.6-.6 2-4.2Z"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
  ),
  pin: () => (
    <>
      <path d="M7.2 3.4h5.6l-.8 5.2 2.4 2.6H5.6L8 8.6l-.8-5.2Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M10 11.2v5.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </>
  ),
  command: () => (
    <path
      d="M7 7h6v6H7V7Zm0 0H5.5A1.5 1.5 0 1 1 7 5.5V7Zm6 0h1.5A1.5 1.5 0 1 0 13 5.5V7Zm0 6h1.5a1.5 1.5 0 1 1-1.5 1.5V13Zm-6 0H5.5A1.5 1.5 0 1 0 7 14.5V13Z"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
  ),
  team: () => (
    <>
      <circle cx="7.5" cy="7.2" r="2.6" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="13.6" cy="8.2" r="2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M3.2 16a4.4 4.4 0 0 1 8.6 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M12.6 13.2a3.4 3.4 0 0 1 4.4 2.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </>
  ),
  repeat: () => (
    <>
      <path d="M4 8.4a5 5 0 0 1 9.2-2.6M16 11.6a5 5 0 0 1-9.2 2.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M13.4 3.2v2.8h-2.8M6.6 16.8V14h2.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  upload: () => (
    <>
      <path d="M10 12.2V3.8M6.6 7.2 10 3.8l3.4 3.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4.4 15.4h11.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </>
  ),
  copy: () => (
    <>
      <rect x="7" y="7" width="9" height="9" rx="1.6" stroke="currentColor" strokeWidth="1.4" />
      <path d="M13 7V5.4A1.4 1.4 0 0 0 11.6 4H5.4A1.4 1.4 0 0 0 4 5.4v6.2A1.4 1.4 0 0 0 5.4 13H7" stroke="currentColor" strokeWidth="1.4" />
    </>
  ),
  link: () => (
    <>
      <path d="M8.6 11.4a3 3 0 0 0 4.2 0l2.2-2.2a3 3 0 0 0-4.2-4.2l-1 1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M11.4 8.6a3 3 0 0 0-4.2 0L5 10.8a3 3 0 0 0 4.2 4.2l1-1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </>
  ),
  logout: () => (
    <>
      <path d="M12 6.2V4.8a1.2 1.2 0 0 0-1.2-1.2H5a1.2 1.2 0 0 0-1.2 1.2v10.4A1.2 1.2 0 0 0 5 16.4h5.8a1.2 1.2 0 0 0 1.2-1.2v-1.4" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M8.4 10h8M13.8 7.4 16.4 10l-2.6 2.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  /**
   * The brand mark: a square with a T cut out of it and a pennant under the
   * crossbar. Filled, drawn at 62.09 units and scaled into the 20 box, so it
   * holds at 14px in a footer and at 36px on the sign-in card.
   */
  logo: () => (
    <path
      fill="currentColor"
      transform="scale(0.32211)"
      d="M0,0v62.09h62.09V0H0ZM36.5,53.36v-27.33h16.86l-16.86,27.33ZM53.36,19.04h-23.83v34.32h-10.32V19.04h-10.49v-10.32h44.63v10.32Z"
    />
  ),
};

export type IconName = keyof typeof paths;

export function Icon({
  name,
  size = 20,
  className,
  ...props
}: { name: IconName; size?: number; className?: string } & SVGProps<SVGSVGElement>) {
  const Shape = paths[name];
  if (!Shape) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      aria-hidden
      focusable="false"
      {...props}
    >
      <Shape />
    </svg>
  );
}
